import { useEffect, useRef, useState } from 'react';

type Dot = {
  x: number;
  y: number;
  size: number;
  color: string;
  speedX: number;
  speedY: number;
  opacity: number;
  id: number; // Unique identifier for each dot
  pulsePhase: number; // For controlling pulse animations
};

type Line = {
  dotA: number;
  dotB: number;
  opacity: number;
  pulseDirection: 'left-to-right' | 'right-to-left';
  pulseProgress: number; // 0 to 1, controls the pulse animation
  pulseSpeed: number; // How fast the pulse moves
  pulseActive: boolean; // Whether this line is currently pulsing
  lastPulseTime: number; // When the last pulse occurred
  pulseInterval: number; // Time between pulses
};

// Helper function to calculate the distance from a point to a line segment
const distanceFromPointToLine = (
  px: number, py: number,  // Point coordinates
  x1: number, y1: number,  // Line start
  x2: number, y2: number   // Line end
): number => {
  // Calculate the squared length of the line segment
  const lengthSquared = Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2);
  
  // If the line segment is actually a point, just calculate the distance to that point
  if (lengthSquared === 0) return Math.sqrt(Math.pow(px - x1, 2) + Math.pow(py - y1, 2));
  
  // Calculate the projection of the point onto the line
  const t = Math.max(0, Math.min(1, ((px - x1) * (x2 - x1) + (py - y1) * (y2 - y1)) / lengthSquared));
  
  // Calculate the closest point on the line segment
  const projectionX = x1 + t * (x2 - x1);
  const projectionY = y1 + t * (y2 - y1);
  
  // Return the distance to the closest point
  return Math.sqrt(Math.pow(px - projectionX, 2) + Math.pow(py - projectionY, 2));
};

export function AnimatedBackground() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dots, setDots] = useState<Dot[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [mousePosition, setMousePosition] = useState<{x: number, y: number} | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const animationRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);

  const purpleColors = [
    'rgba(147, 51, 234, 0.25)', // violet-600
    'rgba(139, 92, 246, 0.25)', // violet-500
    'rgba(167, 139, 250, 0.25)', // violet-400
    'rgba(192, 132, 252, 0.25)', // violet-300
    'rgba(109, 40, 217, 0.3)', // violet-700
    'rgba(124, 58, 237, 0.2)' // violet-600
  ];

  // Initialize dots
  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateDimensions = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        setDimensions({ width: clientWidth, height: clientHeight });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Helper function to calculate distance between two dots
  const calculateDistance = (dotA: Dot, dotB: Dot) => {
    const dx = dotA.x - dotB.x;
    const dy = dotA.y - dotB.y;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Track mouse position for cursor-following electrification
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleMouseEnter = () => {
      setIsHovering(true);
    };

    const handleMouseLeave = () => {
      setIsHovering(false);
      setMousePosition(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseenter', handleMouseEnter);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseenter', handleMouseEnter);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  // Create dots and lines when dimensions change
  useEffect(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return;

    const dotCount = Math.floor((dimensions.width * dimensions.height) / 15000); // Original density
    const newDots: Dot[] = [];
    const now = Date.now();

    for (let i = 0; i < dotCount; i++) {
      newDots.push({
        id: i,
        x: Math.random() * dimensions.width,
        y: Math.random() * dimensions.height,
        size: Math.random() * 5 + 2, // 2-7px - smaller dots
        color: purpleColors[Math.floor(Math.random() * purpleColors.length)],
        speedX: (Math.random() - 0.5) * 0.4, // -0.2 to 0.2
        speedY: (Math.random() - 0.5) * 0.4, // -0.2 to 0.2
        opacity: Math.random() * 0.5 + 0.2, // 0.2-0.7
        pulsePhase: Math.random() * Math.PI * 2 // Random starting phase
      });
    }

    setDots(newDots);

    // Create lines between dots that are close enough
    const newLines: Line[] = [];
    const maxDistance = Math.min(dimensions.width, dimensions.height) * 0.15; // Maximum line length

    for (let i = 0; i < newDots.length; i++) {
      for (let j = i + 1; j < newDots.length; j++) {
        const distance = calculateDistance(newDots[i], newDots[j]);
        
        if (distance < maxDistance) {
          const pulseDirection = Math.random() > 0.5 ? 'left-to-right' : 'right-to-left';
          newLines.push({
            dotA: newDots[i].id,
            dotB: newDots[j].id,
            opacity: Math.max(0.03, 0.15 - (distance / maxDistance) * 0.15), // Lines fade as they get longer
            pulseDirection,
            pulseProgress: 0,
            pulseSpeed: 0.003 + Math.random() * 0.004, // Faster pulse speed (travels quicker)
            pulseActive: false,
            lastPulseTime: now - Math.random() * 5000, // Stagger initial pulse times
            pulseInterval: 2500 + Math.random() * 5000 // Keeping the same interval between pulses
          });
        }
      }
    }

    setLines(newLines);
  }, [dimensions]);

  // Animate dots and lines
  useEffect(() => {
    if (dots.length === 0 || lines.length === 0) return;

    const animate = (time: number) => {
      const deltaTime = lastTimeRef.current ? time - lastTimeRef.current : 0;
      lastTimeRef.current = time;
      const now = Date.now();

      // Update dots
      setDots(prevDots => 
        prevDots.map(dot => {
          let newX = dot.x + dot.speedX;
          let newY = dot.y + dot.speedY;

          // Boundary check with bounce effect
          if (newX <= 0 || newX >= dimensions.width) {
            dot.speedX *= -1;
            newX = dot.x + dot.speedX;
          }
          
          if (newY <= 0 || newY >= dimensions.height) {
            dot.speedY *= -1;
            newY = dot.y + dot.speedY;
          }

          return {
            ...dot,
            x: newX,
            y: newY
          };
        })
      );

      // Update lines and their pulse animations
      setLines(prevLines => 
        prevLines.map(line => {
          // Check if it's time to start a new pulse
          let { pulseActive, pulseProgress, pulseSpeed, lastPulseTime, pulseInterval } = line;
          
          const dotA = getDotById(line.dotA);
          const dotB = getDotById(line.dotB);
          
          // Cursor interaction - activate pulse when mouse is near a line
          if (isHovering && mousePosition && dotA && dotB) {
            // Calculate distance from mouse to line
            const mouseToLineDistance = distanceFromPointToLine(
              mousePosition.x, 
              mousePosition.y, 
              dotA.x, 
              dotA.y, 
              dotB.x, 
              dotB.y
            );
            
            // Activate pulse if mouse is close to the line (within 100px)
            if (mouseToLineDistance < 100 && !pulseActive) {
              pulseActive = true;
              pulseProgress = 0;
              lastPulseTime = now;
              
              // Set direction based on mouse position
              line.pulseDirection = mousePosition.x < (dotA.x + dotB.x) / 2 ? 'left-to-right' : 'right-to-left';
              
              // Increase speed for mouse-triggered pulses
              pulseSpeed = 0.008;
            }
          }
          // Regular timed pulse if not mouse-triggered
          else if (!pulseActive && now - lastPulseTime > pulseInterval) {
            pulseActive = true;
            pulseProgress = 0;
            lastPulseTime = now;
          }
          
          // Update pulse progress if active
          if (pulseActive) {
            pulseProgress += pulseSpeed * deltaTime;
            
            // Reset when pulse is complete
            if (pulseProgress >= 1) {
              pulseActive = false;
              pulseProgress = 0;
            }
          }
          
          return {
            ...line,
            pulseActive,
            pulseProgress,
            lastPulseTime
          };
        })
      );

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, [dots, lines, dimensions, mousePosition, isHovering]);

  // Function to get SVG path for a line
  const getLinePath = (dotA: Dot, dotB: Dot) => {
    return `M${dotA.x},${dotA.y} L${dotB.x},${dotB.y}`;
  };

  // Function to get line gradient for pulse effect
  const getLineGradient = (line: Line, dotA: Dot, dotB: Dot) => {
    const { pulseActive, pulseProgress, pulseDirection } = line;
    
    if (!pulseActive) {
      return `rgba(147, 51, 234, ${line.opacity})`;
    }
    
    // Calculate gradient stops based on pulse direction and progress
    const startColor = 'rgba(167, 139, 250, 0.7)'; // Bright violet
    const endColor = `rgba(147, 51, 234, ${line.opacity})`; // Base violet
    
    if (pulseDirection === 'left-to-right') {
      const pos = pulseProgress * 100;
      return `linear-gradient(90deg, 
        ${endColor} 0%, 
        ${endColor} ${Math.max(0, pos - 20)}%, 
        ${startColor} ${pos}%, 
        ${endColor} ${Math.min(100, pos + 20)}%, 
        ${endColor} 100%)`;
    } else {
      const pos = (1 - pulseProgress) * 100;
      return `linear-gradient(90deg, 
        ${endColor} 0%, 
        ${endColor} ${Math.max(0, pos - 20)}%, 
        ${startColor} ${pos}%, 
        ${endColor} ${Math.min(100, pos + 20)}%, 
        ${endColor} 100%)`;
    }
  };

  // Find a dot by ID
  const getDotById = (id: number) => dots.find(dot => dot.id === id);

  return (
    <div 
      ref={containerRef} 
      className="fixed inset-0 w-full h-full z-20 overflow-hidden"
    >
      {/* Lines between dots */}
      <svg className="absolute inset-0 w-full h-full">
        {lines.map((line, index) => {
          const dotA = getDotById(line.dotA);
          const dotB = getDotById(line.dotB);
          
          if (!dotA || !dotB) return null;
          
          return (
            <line
              key={`line-${index}`}
              x1={dotA.x}
              y1={dotA.y}
              x2={dotB.x}
              y2={dotB.y}
              strokeWidth="0.5"
              stroke={line.pulseActive ? "#a78bfa" : "rgba(147, 51, 234, 0.15)"}
              strokeOpacity={line.pulseActive ? Math.min(0.7, line.opacity * 3) : line.opacity}
              strokeDasharray="2,2"
              style={{
                transition: "stroke 0.3s ease",
                filter: line.pulseActive ? "drop-shadow(0 0 3px rgba(167, 139, 250, 0.8))" : "none"
              }}
            />
          );
        })}
      </svg>

      {/* Dots */}
      {dots.map((dot, index) => (
        <div
          key={`dot-${index}`}
          className="absolute rounded-full transition-transform ease-linear"
          style={{
            left: `${dot.x}px`,
            top: `${dot.y}px`,
            width: `${dot.size}px`,
            height: `${dot.size}px`,
            backgroundColor: dot.color,
            opacity: dot.opacity,
            boxShadow: `0 0 ${dot.size}px ${dot.color.replace(/, \d+\.\d+\)/, ', 0.4)')}`,
            transform: `translate(-50%, -50%)`,
            transitionDuration: '50ms',
            zIndex: 5
          }}
        />
      ))}
      
      {/* Mouse position indicator */}
      {mousePosition && isHovering && (
        <div
          className="absolute rounded-full pointer-events-none transition-all duration-300 ease-out"
          style={{
            left: `${mousePosition.x}px`,
            top: `${mousePosition.y}px`,
            width: '160px',
            height: '160px',
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, rgba(167, 139, 250, 0.1) 0%, rgba(147, 51, 234, 0.03) 50%, rgba(0, 0, 0, 0) 70%)',
            zIndex: 5
          }}
        />
      )}
    </div>
  );
}